package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.produceState
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.color
import dev.lingvo.site.components.localeLabel
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.RenderDto
import dev.lingvo.shared.StorePlatform
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.screenshotRenderImage
import dev.lingvo.site.components.studio.compose.CaptionCell
import dev.lingvo.site.components.studio.compose.ComposeCaption
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSegmented
import dev.lingvo.site.ds.LSegmentedOption
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.awaitCancellation
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Text
import org.w3c.files.Blob

/** A failed image stays distinct from an image whose private bytes are still loading. */
internal data class RenderPreviewState(val urls: Map<String, String> = emptyMap(), val failed: Set<String> = emptySet())

/** The preview fetches private bytes under the session and owns every URL until its images leave. */
@Composable
internal fun renderObjectUrls(slug: String, platform: StorePlatform, renders: List<RenderDto>, retry: Int = 0,
    load: suspend (String) -> Blob = { ApiClient.screenshotRenderImage(slug, platform, it) },
): RenderPreviewState {
    val ids = renders.map { it.id }
    val urls by produceState(RenderPreviewState(), slug, platform, ids, retry) {
        val owned = mutableMapOf<String, String>()
        val failed = mutableSetOf<String>()
        value = RenderPreviewState()
        try {
            for (id in ids) {
                try { owned[id] = createRenderUrl(load(id)) }
                catch (cancelled: CancellationException) { throw cancelled }
                catch (_: Throwable) { failed += id }
                value = RenderPreviewState(owned.toMap(), failed.toSet())
            }
            awaitCancellation()
        } catch (cancelled: CancellationException) { throw cancelled }
        finally { owned.values.forEach(::revokeRenderUrl) }
    }
    return urls.copy(urls = urls.urls.filterKeys { it in ids }, failed = urls.failed.intersect(ids.toSet()))
}

/** A generic store-shaped preview uses the project's images without imitating either store's chrome. */
@Composable
fun StorePageSimulator(
    slug: String, platform: StorePlatform, locale: String, locales: List<String>, theme: String,
    renders: List<RenderDto>, captions: Map<CaptionCell, ComposeCaption>, name: String,
    onPlatform: (StorePlatform) -> Unit, onLocale: (String) -> Unit, onTheme: (String) -> Unit,
    loadImage: suspend (String) -> Blob = { ApiClient.screenshotRenderImage(slug, platform, it) },
) {
    val palette = (if (theme == "dark") ColorMode.DARK else ColorMode.LIGHT).toSitePalette()
    var retry by remember(slug, platform, locale) { mutableStateOf(0) }
    val muted = Modifier.color(palette.textSecondary)
    val previews = renderObjectUrls(slug, platform, renders, retry, loadImage)
    val index = locales.indexOf(locale).coerceAtLeast(0)
    Column(Modifier.gap(Space.MD).styleModifier { property("min-width", "0"); property("max-width", "100%") }) {
        LSegmented(listOf(LSegmentedOption(StorePlatform.APP_STORE, "App Store"), LSegmentedOption(StorePlatform.GOOGLE_PLAY, "Google Play")),
            platform, onPlatform, label = t("app.projects.slug.screenshots.store_page_simulator.preview_platform", "Preview platform"))
        LSegmented(listOf(LSegmentedOption("light", t("app.projects.slug.screenshots.store_page_simulator.light", "Light")), LSegmentedOption("dark", t("app.projects.slug.screenshots.store_page_simulator.dark", "Dark"))),
            theme, onTheme, label = t("app.projects.slug.screenshots.store_page_simulator.preview_appearance", "Preview appearance"))
        LSelect(locale, locales.map { LSelectOption(it, localeLabel(it, "")) }, onLocale, label = t("app.projects.slug.screenshots.store_page_simulator.preview_language", "Preview language"))
        Row(Modifier.gap(Space.SM)) {
            LButton("←", { onLocale(locales[(index - 1 + locales.size) % locales.size]) }, variant = LButtonVariant.GHOST,
                enabled = locales.size > 1, accessibleLabel = t("app.projects.slug.screenshots.store_page_simulator.previous_language", "Previous language"))
            LButton("→", { onLocale(locales[(index + 1) % locales.size]) }, variant = LButtonVariant.GHOST,
                enabled = locales.size > 1, accessibleLabel = t("app.projects.slug.screenshots.store_page_simulator.next_language", "Next language"))
        }
        Div(Modifier.styleModifier {
            property("width", "100%"); property("max-width", "390px"); property("box-sizing", "border-box")
            property("background", palette.surface.toString()); property("color", palette.textPrimary.toString())
            property("padding", Space.MD.toString()); property("border", "1px solid ${palette.border}")
            property("border-radius", Space.MD.toString())
        }.toAttrs { attr("data-store-simulator", platform.id); attr("data-theme", theme); attr("lang", locale) }) {
            Div(Modifier.styleModifier { property("display", "flex"); property("gap", Space.MD.toString()); property("align-items", "center") }.toAttrs()) {
                Div(Modifier.styleModifier { property("width", "48px"); property("height", "48px"); property("background", palette.accentSubtle.toString()) }.toAttrs { attr("aria-hidden", "true") }) { Text(name.take(1)) }
                Column { Text(name); LMuted(t("app.projects.slug.screenshots.store_page_simulator.your_app_in_every_language", "Your app, in every language"), muted) }
            }
            val label = t("app.projects.slug.screenshots.store_page_simulator.screenshots", "Screenshots")
            Div(Modifier.styleModifier {
                property("display", "flex"); property("gap", Space.SM.toString()); property("overflow-x", "auto")
                property("scroll-snap-type", "x mandatory"); property("max-width", "100%")
            }.toAttrs { attr("role", "region"); attr("aria-roledescription", "carousel"); attr("aria-label", label); tabIndex(0) }) {
                renders.forEachIndexed { i, render ->
                    val caption = captions[CaptionCell(render.slotKey, render.locale)]?.text.orEmpty()
                    Div(Modifier.styleModifier {
                        property("flex", "0 0 72%"); property("min-width", "0"); property("scroll-snap-align", "start")
                    }.toAttrs { attr("role", "group"); attr("aria-roledescription", "slide") }) {
                        previews.urls[render.id]?.let { url -> Img(url, alt = caption, attrs = {
                            attr("lang", render.locale); attr("width", render.width.toString()); attr("height", render.height.toString())
                            style { property("width", "100%"); property("height", "auto"); property("display", "block") }
                        }) }
                        LMuted(tf("shots.simulator.position", "Screenshot {index} of {total}", "index" to i + 1, "total" to renders.size), muted)
                    }
                }
            }
            if (previews.failed.isNotEmpty()) {
                LMuted(t("app.projects.slug.screenshots.store_page_simulator.some_screenshot_previews_could_not_load", "Some screenshot previews could not load. Retry to see the complete set."), muted)
                LButton(t("app.projects.slug.screenshots.store_page_simulator.retry_previews", "Retry previews"), { retry++ }, variant = LButtonVariant.SECONDARY)
            }
            if (renders.isEmpty()) LMuted(t("app.projects.slug.screenshots.store_page_simulator.no_composed_screenshots_in_this_language", "No composed screenshots in this language yet."), muted)
            LMuted(t("app.projects.slug.screenshots.store_page_simulator.see_your_screenshots_together_before_you", "See your screenshots together before you send them to the store."), muted)
            LMuted(t("app.projects.slug.screenshots.store_page_simulator.this_preview_shows_the_sets_proportions", "This preview shows the set's proportions and reading order."), muted)
        }
    }
}

private fun createRenderUrl(blob: Blob): String = window.asDynamic().URL.createObjectURL(blob) as String
private fun revokeRenderUrl(url: String) { window.asDynamic().URL.revokeObjectURL(url) }
